import React, { useEffect } from "react";
import { App, Modal, Button, Typography } from "antd";
import { ExternalLinkIcon, ClockIcon } from "lucide-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useNavigate } from "react-router";
import { NotificationItem, useMarkNotificationReadMutation } from "@/actions/notification.actions";
import { getNotificationIcon, getTypeTag } from "./notificationIcons";

dayjs.extend(relativeTime);

interface ViewNotificationModalProps {
  open: boolean;
  onClose: () => void;
  notification: NotificationItem | null;
  onNavigate?: (link: string) => void;
}

const getActionLabel = (type?: NotificationItem["type"], link?: string | null) => {
  if (!link) return "Open Link";
  if (link.includes("payments")) return "View Payment";
  if (link.includes("leads")) return "View Lead";
  if (link.includes("campaigns")) return "View Campaign";
  if (type === "customer_payment" || type === "supplier_payment") return "Go to Payment";
  if (type === "followup") return "Go to Lead";
  return "Open Related Resource";
};

/** Previews a notification and marks it read once opened. */
export const ViewNotificationModal: React.FC<ViewNotificationModalProps> = ({
  open,
  onClose,
  notification,
  onNavigate,
}) => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const markReadMutation = useMarkNotificationReadMutation();

  useEffect(() => {
    if (open && notification && !notification.isRead) {
      markReadMutation.mutate(notification.id, { onError: () => message.error("Failed to mark notification as read") });
    }
  }, [open, notification]);

  if (!notification) return null;

  const link = notification.data?.link;
  const linkPath = typeof link === "string" ? link.split(/[?#]/)[0].replace(/\/+$/, "") : "";
  const targetLink = typeof link === "string" && /^\/(?![\/\\])[^\\\s]*$/.test(link)
    && linkPath && linkPath !== "/notifications" ? link : null;
  const timeAgo = dayjs(notification.triggeredAt).fromNow();

  const handleOpenLink = () => {
    onClose();
    if (targetLink) {
      if (onNavigate) {
        onNavigate(targetLink);
      } else {
        navigate(targetLink);
      }
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={560}
      centered
      style={{ maxWidth: "calc(100vw - 1.5rem)" }}
      title={
        <div className="flex items-center gap-3 pr-6">
          {getNotificationIcon(notification.type, notification, "lg")}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              {getTypeTag(notification.type)}
              <span className="text-[11px] text-gray-600 font-normal flex items-center gap-1">
                <ClockIcon size={12} />
                {timeAgo}
              </span>
            </div>
            <Typography.Title level={5} className="!m-0 !text-base !font-semibold !text-[#131B2A] break-words">
              {notification.title}
            </Typography.Title>
          </div>
        </div>
      }
      styles={{
        footer: { marginTop: 24 },
        body: { paddingTop: 12 },
      }}
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button onClick={onClose}>
            Close
          </Button>
          {targetLink && (
            <Button
              type="primary"
              icon={<ExternalLinkIcon size={14} />}
              onClick={handleOpenLink}
              className="!bg-[#131B2A] hover:!bg-[#1E293B]"
            >
              {getActionLabel(notification.type, targetLink)}
            </Button>
          )}
        </div>
      }
    >
      <div className="mt-2 space-y-4">
        {/* Full untruncated message */}
        <div className="rounded-xl border border-gray-200/80 bg-gray-50/70 p-4 max-h-[360px] overflow-y-auto">
          <Typography.Paragraph className="!m-0 text-sm !text-gray-800 leading-relaxed whitespace-pre-wrap break-words">
            {notification.message}
          </Typography.Paragraph>
        </div>

        {/* Optional Metadata pills */}
        {(Boolean(notification.data?.leadId) || Boolean(notification.data?.scheduleId)) && (
          <div className="flex flex-wrap items-center gap-2 pt-1 text-xs text-gray-500">
            {Boolean(notification.data?.leadId) && (
              <span className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-600 font-medium">
                Lead #{String(notification.data?.leadId)}
              </span>
            )}
            {Boolean(notification.data?.scheduleId) && (
              <span className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-600 font-medium">
                Schedule attached
              </span>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

